import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  FileText, Shield, AlertTriangle, Search, Activity, Download,
  CheckCircle, XCircle, Clock, Database, Scale, Loader2, Info,
  BookOpen, Gavel, Users, Eye, BarChart3, Flag, ArrowLeft
} from "lucide-react";
import LinkToCase from "@/components/shared/LinkToCase";

const SOURCE_COLORS = {
  RECORD: { bg: "bg-blue-100", text: "text-blue-800", border: "border-blue-200", label: "RECORD" },
  AI_ANALYSIS: { bg: "bg-amber-100", text: "text-amber-800", border: "border-amber-200", label: "AI ANALYSIS" },
  USER_INPUT: { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-200", label: "USER INPUT" },
  UNAVAILABLE: { bg: "bg-slate-100", text: "text-slate-500", border: "border-slate-200", label: "UNAVAILABLE" },
};

const SourceBadge = ({ type }) => {
  const c = SOURCE_COLORS[type] || SOURCE_COLORS.UNAVAILABLE;
  return <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold ${c.bg} ${c.text} ${c.border} border`} data-testid={`badge-${type}`}>{c.label}</span>;
};

const RiskBanner = ({ level, score, sourceType }) => {
  const colors = {
    CRITICAL: "bg-red-50 border-red-300 text-red-800",
    HIGH: "bg-red-50 border-red-200 text-red-700",
    ELEVATED: "bg-amber-50 border-amber-200 text-amber-800",
    MODERATE: "bg-amber-50 border-amber-200 text-amber-700",
    LOW: "bg-green-50 border-green-200 text-green-800",
    UNDETERMINED: "bg-slate-50 border-slate-200 text-slate-600",
  };
  return (
    <div className={`p-3 rounded-lg border-2 ${colors[level] || colors.UNDETERMINED}`}>
      <div className="flex items-center justify-between">
        <span className="text-lg font-bold">{level} ({score}/100)</span>
        <SourceBadge type={sourceType || "AI_ANALYSIS"} />
      </div>
    </div>
  );
};

export default function CourtIntelligencePage() {
  const [tab, setTab] = useState("new");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [history, setHistory] = useState([]);
  const [dockets, setDockets] = useState([]);
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({
    case_title: "", docket_number: "", court_name: "", court_type: "federal",
    jurisdiction: "", case_type: "", filing_date: "",
    plaintiff_name: "", plaintiff_type: "", defendant_name: "", defendant_type: "",
    plaintiff_attorney: "", defendant_attorney: "", judge_name: "",
    linked_docket_id: "", linked_investigation_id: "", linked_insurance_report_id: "",
    verify_email: "", verify_phone: "", verify_address: "",
    include_judge_profile: true, include_attorney_profiles: true,
    include_party_history: true, include_risk_assessment: true,
    include_litigation_history: true, include_verification: true,
    notes: "",
  });

  useEffect(() => {
    axios.get(`${API}/court-intelligence/reports`).then(r => setHistory(r.data.reports || [])).catch(() => {});
    axios.get(`${API}/court-filings/dockets`).then(r => setDockets(r.data.dockets || [])).catch(() => {});
    axios.get(`${API}/court-intelligence/providers`).then(r => setProviders(r.data.providers || [])).catch(() => {});
  }, []);

  const generateReport = async () => {
    if (!form.case_title) return;
    setLoading(true);
    setReport(null);
    try {
      const payload = { ...form };
      if (!payload.linked_docket_id) delete payload.linked_docket_id;
      if (!payload.linked_investigation_id) delete payload.linked_investigation_id;
      if (!payload.linked_insurance_report_id) delete payload.linked_insurance_report_id;
      const { data } = await axios.post(`${API}/court-intelligence/generate`, payload);
      setReport(data);
      setTab("report");
      axios.get(`${API}/court-intelligence/reports`).then(r => setHistory(r.data.reports || []));
    } catch (err) { alert(err.response?.data?.detail || "Report generation failed"); }
    finally { setLoading(false); }
  };

  const viewReport = async (id) => {
    try {
      const { data } = await axios.get(`${API}/court-intelligence/reports/${id}`);
      setReport(data.data);
      setTab("report");
    } catch {}
  };

  const renderSectionContent = (section) => {
    const stype = section.section_type;
    const items = section.items || [];

    if (stype === "empty") {
      return <p className="text-sm text-slate-500 italic py-4">{items[0]?.value}</p>;
    }

    if (stype === "structured") {
      const labels = section.field_labels || [];
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-start gap-2 p-2 bg-slate-50 rounded text-sm">
              <span className="font-semibold text-slate-600 min-w-[120px]">{labels[i] || `Field ${i + 1}`}:</span>
              <span className="flex-1">{String(item.value)}</span>
              <SourceBadge type={item.source_type} />
            </div>
          ))}
        </div>
      );
    }

    if (stype === "timeline") {
      return (
        <div className="space-y-3">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-800 text-white text-xs">
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Doc #</th>
                <th className="p-2 text-left">Filing</th>
                <th className="p-2 text-left">Filed By</th>
                <th className="p-2 text-left">Description</th>
                <th className="p-2 text-left">Source</th>
              </tr></thead>
              <tbody>
                {items.map((item, i) => {
                  const v = item.value || {};
                  return (
                    <tr key={i} className="border-b hover:bg-slate-50" data-testid={`timeline-row-${i}`}>
                      <td className="p-2 font-mono text-xs">{v.date}</td>
                      <td className="p-2 text-xs">{v.doc_number}</td>
                      <td className="p-2 font-semibold">{v.type}</td>
                      <td className="p-2 text-xs">{v.filed_by}</td>
                      <td className="p-2 text-xs">{v.description}</td>
                      <td className="p-2"><SourceBadge type={item.source_type} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {(section.deadlines || []).length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-1"><Clock className="w-4 h-4 text-amber-600" />Deadlines</h4>
              <div className="space-y-1">
                {section.deadlines.map((dl, i) => {
                  const v = dl.value || {};
                  const today = new Date().toISOString().slice(0, 10);
                  const overdue = v.status === "pending" && v.deadline_date < today;
                  return (
                    <div key={i} className={`flex items-center gap-2 p-2 rounded text-xs border ${overdue ? "bg-red-50 border-red-200" : "bg-slate-50"}`}>
                      <span className="font-mono w-24">{v.deadline_date}</span>
                      <span className="flex-1 font-medium">{v.deadline_name}</span>
                      <span className={`font-bold ${overdue ? "text-red-700" : v.status === "met" ? "text-green-700" : "text-amber-700"}`}>{overdue ? "OVERDUE" : v.status?.toUpperCase()}</span>
                      <SourceBadge type="RECORD" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (stype === "ai_analysis") {
      const risk = section.risk_level || {};
      const score = section.risk_score || {};
      return (
        <div className="space-y-4">
          <RiskBanner level={risk.value || risk} score={score.value || score} sourceType="AI_ANALYSIS" />
          {items.map((item, i) => (
            <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm flex-1">{item.value}</p>
              <SourceBadge type="AI_ANALYSIS" />
            </div>
          ))}
          {(section.strategic_notes || []).length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">Strategic Notes <SourceBadge type="AI_ANALYSIS" /></h4>
              <ul className="space-y-1.5">
                {section.strategic_notes.map((n, i) => (
                  <li key={i} className="text-sm flex items-start gap-2 p-2 bg-amber-50/50 rounded"><span className="text-amber-600 font-bold shrink-0">{i + 1}.</span>{n.value}</li>
                ))}
              </ul>
            </div>
          )}
          {(section.red_flags || []).length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2 text-red-700 flex items-center gap-1"><Flag className="w-4 h-4" />Red Flags <SourceBadge type="AI_ANALYSIS" /></h4>
              <ul className="space-y-1">
                {section.red_flags.map((f, i) => (
                  <li key={i} className="text-sm text-red-700 flex items-start gap-2"><XCircle className="w-3 h-3 mt-0.5 shrink-0" />{f.value}</li>
                ))}
              </ul>
            </div>
          )}
          {(section.recommendations || []).length > 0 && (
            <div>
              <h4 className="font-semibold text-sm mb-2 flex items-center gap-1">Recommendations <SourceBadge type="AI_ANALYSIS" /></h4>
              <ol className="space-y-1.5 list-decimal list-inside">
                {section.recommendations.map((r, i) => (
                  <li key={i} className="text-sm">{r.value}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      );
    }

    // findings, verification, records, insurance, profile
    return (
      <div className="space-y-2">
        {section.risk_assessment && (
          <RiskBanner
            level={section.risk_assessment.value?.level}
            score={section.risk_assessment.value?.score}
            sourceType={section.risk_assessment.source_type}
          />
        )}
        {items.map((item, i) => {
          const v = item.value;
          if (typeof v === "object" && v !== null) {
            return (
              <div key={i} className="p-3 bg-slate-50 rounded-lg border text-sm">
                <div className="flex items-start justify-between mb-1">
                  <span className="text-xs text-slate-400">{item.source_name}</span>
                  <SourceBadge type={item.source_type} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 text-xs">
                  {Object.entries(v).filter(([k]) => !["fraud_indicators", "verification_results", "data_sources", "citation"].includes(k)).map(([k, val]) => (
                    <div key={k}>
                      <span className="text-slate-500">{k.replace(/_/g, " ")}:</span>{" "}
                      <span className={`font-medium ${k.includes("fraud") && typeof val === "number" && val >= 75 ? "text-red-600" : ""}`}>
                        {typeof val === "boolean" ? (val ? "Yes" : "No") : String(val)}
                      </span>
                    </div>
                  ))}
                </div>
                {v.fraud_indicators?.length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 rounded border border-red-200">
                    <span className="text-xs text-red-700 font-bold">Fraud Indicators:</span>
                    <ul className="mt-1">{v.fraud_indicators.map((f, j) => <li key={j} className="text-xs text-red-600">{f}</li>)}</ul>
                  </div>
                )}
              </div>
            );
          }
          return <p key={i} className="text-sm flex items-center gap-2">{String(v)} <SourceBadge type={item.source_type} /></p>;
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="court-intelligence-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Court Intelligence Report</h1>
        <p className="text-slate-500 mt-1">Source-verified case intelligence with fact/analysis separation</p>
      </div>

      {/* Source Legend */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg border text-xs" data-testid="source-legend">
        <span className="font-semibold text-slate-700">Source Legend:</span>
        <SourceBadge type="RECORD" /> <span className="text-slate-500">Verified court/API data</span>
        <SourceBadge type="AI_ANALYSIS" /> <span className="text-slate-500">AI-generated inference</span>
        <SourceBadge type="USER_INPUT" /> <span className="text-slate-500">Attorney-provided</span>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="new" data-testid="tab-new-report">New Report</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-view-report">View Report</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History ({history.length})</TabsTrigger>
          <TabsTrigger value="providers" data-testid="tab-providers">Data Providers</TabsTrigger>
        </TabsList>

        {/* ═══ NEW REPORT ═══ */}
        <TabsContent value="new" className="mt-4 space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Scale className="w-5 h-5 text-blue-600" />Case Identification</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Case Title *</Label><Input data-testid="ci-case-title" value={form.case_title} onChange={e => setForm(f => ({...f, case_title: e.target.value}))} placeholder="Smith v. Jones" /></div>
                <div><Label>Docket Number</Label><Input data-testid="ci-docket-number" value={form.docket_number} onChange={e => setForm(f => ({...f, docket_number: e.target.value}))} placeholder="2:24-cv-01234" /></div>
                <div><Label>Court Name</Label><Input data-testid="ci-court-name" value={form.court_name} onChange={e => setForm(f => ({...f, court_name: e.target.value}))} placeholder="C.D. California" /></div>
                <div><Label>Jurisdiction</Label><Input data-testid="ci-jurisdiction" value={form.jurisdiction} onChange={e => setForm(f => ({...f, jurisdiction: e.target.value}))} placeholder="California" /></div>
                <div><Label>Case Type</Label><Input data-testid="ci-case-type" value={form.case_type} onChange={e => setForm(f => ({...f, case_type: e.target.value}))} placeholder="Personal Injury" /></div>
                <div><Label>Judge Name</Label><Input data-testid="ci-judge" value={form.judge_name} onChange={e => setForm(f => ({...f, judge_name: e.target.value}))} placeholder="Hon. Smith" /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" />Parties & Counsel</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label>Plaintiff</Label><Input data-testid="ci-plaintiff" value={form.plaintiff_name} onChange={e => setForm(f => ({...f, plaintiff_name: e.target.value}))} /></div>
                <div><Label>Plaintiff Counsel</Label><Input data-testid="ci-plaintiff-atty" value={form.plaintiff_attorney} onChange={e => setForm(f => ({...f, plaintiff_attorney: e.target.value}))} /></div>
                <div><Label>Defendant</Label><Input data-testid="ci-defendant" value={form.defendant_name} onChange={e => setForm(f => ({...f, defendant_name: e.target.value}))} /></div>
                <div><Label>Defendant Counsel</Label><Input data-testid="ci-defendant-atty" value={form.defendant_attorney} onChange={e => setForm(f => ({...f, defendant_attorney: e.target.value}))} /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Database className="w-5 h-5 text-blue-600" />Link Existing Data</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">Link existing TrialEze data sources to enrich the report with verified records.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Link Docket</Label>
                  <Select value={form.linked_docket_id || "none"} onValueChange={v => setForm(f => ({...f, linked_docket_id: v === "none" ? "" : v}))}>
                    <SelectTrigger data-testid="link-docket-select"><SelectValue placeholder="Select docket..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No docket</SelectItem>
                      {dockets.map(d => <SelectItem key={d.id} value={d.id}>{d.case_title} ({d.filing_count} filings)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Verify Email</Label><Input data-testid="ci-verify-email" value={form.verify_email} onChange={e => setForm(f => ({...f, verify_email: e.target.value}))} placeholder="party@email.com" /></div>
                <div><Label>Verify Phone</Label><Input data-testid="ci-verify-phone" value={form.verify_phone} onChange={e => setForm(f => ({...f, verify_phone: e.target.value}))} placeholder="+1 555-0123" /></div>
                <div><Label>Verify Address</Label><Input data-testid="ci-verify-address" value={form.verify_address} onChange={e => setForm(f => ({...f, verify_address: e.target.value}))} placeholder="123 Main St, City, State" /></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Report Sections</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: "include_judge_profile", label: "Judge Profile" },
                  { key: "include_attorney_profiles", label: "Attorney Profiles" },
                  { key: "include_party_history", label: "Party History" },
                  { key: "include_risk_assessment", label: "AI Risk Assessment" },
                  { key: "include_litigation_history", label: "Litigation History" },
                  { key: "include_verification", label: "Contact Verification" },
                ].map(s => (
                  <div key={s.key} className={`flex items-center justify-between gap-2 p-3 rounded-lg border ${form[s.key] ? "bg-blue-50 border-blue-300" : "border-slate-200"}`}>
                    <span className="text-sm font-medium">{s.label}</span>
                    <Switch checked={form[s.key]} onCheckedChange={checked => setForm(f => ({...f, [s.key]: checked}))} data-testid={`switch-${s.key}`} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            <strong>Accuracy Notice:</strong> All data in this report is tagged with its source. Items labeled <SourceBadge type="RECORD" /> come from verified court records and API data. Items labeled <SourceBadge type="AI_ANALYSIS" /> are AI-generated inferences that should be independently verified.
          </div>

          <Button data-testid="generate-ci-report-btn" onClick={generateReport} disabled={loading || !form.case_title} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 text-lg">
            {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" />Generating Court Intelligence Report...</> : <><Scale className="w-5 h-5 mr-2" />Generate Court Intelligence Report</>}
          </Button>
        </TabsContent>

        {/* ═══ REPORT VIEW ═══ */}
        <TabsContent value="report" className="mt-4">
          {report ? (
            <div className="space-y-4">
              <Card className="border-l-4 border-l-blue-600">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-bold" data-testid="report-title">{report.case_title}</h2>
                      <p className="text-slate-500 text-sm mt-0.5">{report.court_name} | {report.jurisdiction} | {report.total_sources} data sources verified</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => window.open(`${API}/court-intelligence/reports/${report.id}/export-pdf`, '_blank')} data-testid="export-ci-pdf">
                        <Download className="w-3 h-3 mr-1" />Export PDF
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Sources Banner */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-800">Data Sources Verified ({report.total_sources})</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {report.sources_used?.map((s, i) => (
                    <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">{s}</span>
                  ))}
                </div>
              </div>

              {/* Report Sections */}
              {report.sections?.map((section, i) => (
                <Card key={i} className={section.section_type === "ai_analysis" ? "border-amber-200" : ""} data-testid={`section-${section.section_id}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      {section.section_type === "ai_analysis" ? <AlertTriangle className="w-4 h-4 text-amber-600" /> :
                       section.section_type === "timeline" ? <Clock className="w-4 h-4 text-blue-600" /> :
                       section.section_type === "verification" ? <Shield className="w-4 h-4 text-green-600" /> :
                       section.section_type === "findings" ? <Search className="w-4 h-4 text-purple-600" /> :
                       <FileText className="w-4 h-4 text-blue-600" />}
                      {section.section_title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>{renderSectionContent(section)}</CardContent>
                </Card>
              ))}

              {/* Source Audit Trail */}
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><BookOpen className="w-4 h-4 text-slate-600" />Source Audit Trail</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-slate-800 text-white">
                        <th className="p-2 text-left">Section</th>
                        <th className="p-2 text-left">Source Type</th>
                        <th className="p-2 text-left">Provider</th>
                      </tr></thead>
                      <tbody>
                        {report.source_audit?.map((a, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-2">{a.section}</td>
                            <td className="p-2"><SourceBadge type={a.source} /></td>
                            <td className="p-2">{a.provider}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Disclaimer */}
              <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-lg text-xs text-amber-900" data-testid="report-disclaimer">
                <strong>LEGAL DISCLAIMER:</strong> {report.disclaimer}
              </div>

              <LinkToCase
                itemId={report.id}
                itemType="court_intelligence_report"
                title={`Intelligence Report: ${report.case_title}`}
                summary={`${report.total_sources} sources | ${report.sections?.length} sections`}
              />
            </div>
          ) : (
            <Card><CardContent className="py-12 text-center text-slate-500">
              <Scale className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              No report generated yet. Create a new Court Intelligence Report.
            </CardContent></Card>
          )}
        </TabsContent>

        {/* ═══ HISTORY ═══ */}
        <TabsContent value="history" className="mt-4 space-y-2">
          {history.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-slate-500">No reports yet.</CardContent></Card>
          ) : history.map(r => (
            <Card key={r.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => viewReport(r.id)}>
              <CardContent className="py-3 flex items-center justify-between">
                <div>
                  <span className="font-semibold">{r.case_title}</span>
                  <span className="text-xs text-slate-400 ml-2">{r.court_name}</span>
                  <span className="text-xs ml-2 px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">{r.total_sources} sources</span>
                </div>
                <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* ═══ DATA PROVIDERS ═══ */}
        <TabsContent value="providers" className="mt-4 space-y-3">
          <p className="text-sm text-slate-500 mb-2">Court data providers powering TrialEze intelligence reports.</p>
          {providers.map(p => (
            <Card key={p.id} data-testid={`provider-${p.id}`}>
              <CardContent className="py-4 flex items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{p.name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${p.status === "active" ? "bg-green-100 text-green-700" : p.status === "active_no_key" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                      {p.status === "active" ? "ACTIVE" : p.status === "active_no_key" ? "ACTIVE (No Key)" : "CONFIG NEEDED"}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>
                </div>
                {p.url && <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline shrink-0">{p.url}</a>}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
